import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet } from 'react-router';

import { useAppDispatch } from '../store/store';
import { useAuthContext } from '../context/AuthContext';
import Navigation from './Navigation/Navigation';
import { fetchChannels } from '../store/slices/channels-slice';
import { fetchContracts } from '../store/slices/clients-slice';
import { fetchHubs } from '../store/slices/hubs-slice';
import { fetchSensors } from '../store/slices/sensors-slice';
import { fetchUsers } from '../store/slices/users-slice';
import RegulationsModal from './RegulationsModal/RegulationsModal';
import ChangeLog, { type Change } from './ChangeLog/ChangeLog';
import AppVersion from '../version';
import extractChanges from './ChangeLog/extractChanges';

const ProtectedLayout = () => {
  const { isAuthenticated, user, isStatuteAccepted, updateUserSettings } = useAuthContext();

  const dispatch = useAppDispatch();

  const appVersion = AppVersion.version;

  const [showChangelog, setShowChangelog] = useState(false);

  const changelogData: Change[] | null = useMemo(() => {
    if (!showChangelog || !user) {
      return null;
    }
    const lastVersionFromServer = user.settings?.last_version;
    return [...extractChanges(lastVersionFromServer || 'v0.0.0', appVersion)].reverse();
  }, [appVersion, showChangelog, user]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    dispatch(fetchChannels());
    dispatch(fetchContracts());
    dispatch(fetchHubs());
    dispatch(fetchSensors());
    dispatch(fetchUsers());
  }, [dispatch, isAuthenticated, user]);

  // Update last version on server
  useEffect(() => {
    if (!user || !user.settings) return;

    const lastVersionFromServer = user.settings.last_version;
    if (lastVersionFromServer === appVersion) return;

    if (
      (lastVersionFromServer && lastVersionFromServer !== appVersion) ||
      (user.settings && !lastVersionFromServer)
    ) {
      setShowChangelog(true);
    }
    const updatedSettings = { ...user.settings, last_version: appVersion };
    updateUserSettings(updatedSettings);
  }, [appVersion, updateUserSettings, user]);

  if (!user || !isAuthenticated) return <Navigate to="/login" />;

  return (
    <>
      {!isStatuteAccepted && <RegulationsModal />}
      {showChangelog && changelogData && (
        <ChangeLog data={changelogData} show={showChangelog} setShow={setShowChangelog} />
      )}
      <Navigation />
      <main>
        <Outlet />
      </main>
    </>
  );
};

export default ProtectedLayout;
